// Story 213, adenda C11.4 — what `Settings › Camera control` knows about the VISION_ settings it edits.

export const VISION_ENABLED = 'VISION_ENABLED'
export const VISION_COVER_MODE = 'VISION_COVER_MODE'
export const VISION_MODEL = 'VISION_MODEL'

// C6: the stored values of VISION_COVER_MODE and the i18n key each one is painted with (C7, vocabulary «Protected»).
export const COVER_MODES = [
  { value: 'ALL', text: 'Whole screen', help: 'Everything in MIC is hidden until the phone disappears. The user cannot keep working meanwhile.' },
  { value: 'SENSITIVE_ONLY', text: 'Protected content only', help: 'Only what is marked is hidden; the rest stays visible and usable.' }
]

// Step 3 of C11.4: each rule as a sentence, grouped as the design does.
export const RULE_GROUPS = [
  {
    title: 'Phone in view',
    fields: [
      { key: 'VISION_PHONE_VISIBLE_SECONDS', label: 'Cover if seen for', unit: 's' },
      { key: 'VISION_PHONE_RELEASE_SECONDS', label: 'Show again after', unit: 's without seeing it' }
    ]
  },
  {
    title: 'People',
    fields: [
      { key: 'VISION_NOBODY_SECONDS', label: 'Cover if nobody for', unit: 's' },
      { key: 'VISION_MAX_PEOPLE', label: 'Warn if more than', unit: '' },
      { key: 'VISION_CROWD_SECONDS', label: 'people for', unit: 's' }
    ]
  },
  {
    title: 'No camera',
    fields: [
      { key: 'VISION_NO_FRAME_SECONDS', label: 'Cover if no image arrives for', unit: 's' },
      { key: 'VISION_FIRST_FRAME_SECONDS', label: 'On entering, wait for the first image up to', unit: 's' }
    ]
  }
]

// Step 3, "Advanced settings" (DA-5: thresholds shown as they are, lower detects more).
export const ADVANCED_FIELDS = [
  { key: 'VISION_PHONE_THRESHOLD', label: 'Phone threshold', unit: '' },
  { key: 'VISION_PERSON_THRESHOLD', label: 'People threshold', unit: '' },
  { key: 'VISION_FRAME_INTERVAL_SECONDS', label: 'One image every', unit: 's' },
  { key: 'VISION_PEOPLE_INTERVAL_SECONDS', label: 'Count people every', unit: 's' }
]

export const RULES_HELP = [
  'Without a webcam protected content cannot be viewed.',
  'Lower detects more, with more false alarms',
  'A loose hand does not count as a person'
]

// V3 (Sun's VisionSettings.fromRaw), applied on screen (C11.4, H-14): range, step, whole numbers, open interval
// (thresholds: 0 < x < 1) and the cross rules (S91): people interval ≥ image interval, first image ≥ no image.
export const V3_RANGES = {
  VISION_FRAME_INTERVAL_SECONDS: { min: 0.2, max: 5, step: 0.1 },
  VISION_PEOPLE_INTERVAL_SECONDS: { min: 0.5, max: 30, step: 0.5, atLeast: 'VISION_FRAME_INTERVAL_SECONDS' },
  VISION_PHONE_VISIBLE_SECONDS: { min: 0, max: 10, step: 0.1 },
  VISION_PHONE_RELEASE_SECONDS: { min: 0, max: 30, step: 0.1 },
  VISION_PHONE_THRESHOLD: { min: 0, max: 1, step: 0.05, open: true },
  VISION_PERSON_THRESHOLD: { min: 0, max: 1, step: 0.05, open: true },
  VISION_NOBODY_SECONDS: { min: 5, max: 3600, step: 1 },
  VISION_MAX_PEOPLE: { min: 1, max: 10, step: 1, whole: true },
  // C16.6 (RN141): how many sessions are watched at a time.
  VISION_MAX_WATCHED_SESSIONS: { min: 1, max: 50, step: 1, whole: true },
  // C17.6 (RN157): how many days a phone image is kept.
  VISION_PHONE_EVIDENCE_DAYS: { min: 1, max: 90, step: 1, whole: true },
  VISION_CROWD_SECONDS: { min: 0, max: 600, step: 1 },
  VISION_NO_FRAME_SECONDS: { min: 3, max: 120, step: 1 },
  VISION_FIRST_FRAME_SECONDS: { min: 10, max: 600, step: 1, atLeast: 'VISION_NO_FRAME_SECONDS' }
}

const NUMBER = /^\d+(\.\d+)?$/

// A number as typed, with a decimal comma or point (Sun reads both, S90); NaN when it is not one.
export const parseNumber = (text) => {
  const normalized = String(text).trim().replace(',', '.')
  return NUMBER.test(normalized) ? Number(normalized) : NaN
}

const outOfRange = (value, range) => Number.isNaN(value) ||
  (range.open ? value <= range.min || value >= range.max : value < range.min || value > range.max) ||
  (range.whole === true && !Number.isInteger(value))

// The keys whose value breaks V3, as { key: true }.
export const invalidSettings = (values) => Object.fromEntries(
  Object.entries(V3_RANGES)
    .filter(([key, range]) => {
      if (!(key in values)) {
        return false
      }
      const value = parseNumber(values[key])
      if (outOfRange(value, range)) {
        return true
      }
      return Boolean(range.atLeast) && value < parseNumber(values[range.atLeast])
    })
    .map(([key]) => [key, true])
)

// H-10: every number on the screen with the number format of the MIC language (`0,4` in "es", `0.4` in "en").
const intlLocale = (language) => {
  try {
    return Intl.NumberFormat.supportedLocalesOf([String(language)]).length > 0 ? String(language) : 'en'
  } catch (exception) {
    return 'en'
  }
}

export const formatNumber = (value, language) =>
  new Intl.NumberFormat(intlLocale(language), { maximumFractionDigits: 6, useGrouping: false }).format(value)

export const formatPercent = (value, language) =>
  new Intl.NumberFormat(intlLocale(language), { style: 'percent', maximumFractionDigits: 0 }).format(value)

// A value of Sun as the field shows it; what is not a number is shown as it came.
export const displayValue = (stored, language) => {
  const value = parseNumber(stored)
  return Number.isNaN(value) ? String(stored) : formatNumber(value, language)
}

// What a typed value travels as: with a decimal point (Sun reads both, S90), or as typed if it is not a number.
export const storedValue = (typed) => {
  const value = parseNumber(typed)
  return Number.isNaN(value) ? String(typed) : String(value)
}

// DA-8, RN52: the reference figures of each model, fixed (measured in factory-lab), not computed.
export const MODELS = [
  { value: 'FAST', text: 'Fast', recommended: true, secondsPerImage: 0.4, phonesDetected: 0.87, users: [1, 2], usersText: 'users at a time' },
  { value: 'PRECISE', text: 'Precise', recommended: false, secondsPerImage: 0.8, phonesDetected: 0.91, users: [1], usersText: 'user at a time' }
]

// C15.3 (H-C15-3): the three types of "Single items", in the order of the selector. Fabrication orders are protected by
// the cascade of their product or order, and widgets are not chosen here.
export const SINGLE_ITEM_TYPES = [
  { type: 'PART_REFERENCE', text: 'Product' },
  { type: 'ORDER', text: 'Order' },
  { type: 'DOCUMENT', text: 'Document' }
]

const countOf = (value) => (typeof value === 'number' && value > 0 ? value : 0)

// C11.10 (S298): what a protected product or order also protects, with the two plural keys; a count at 0 leaves its part
// out and with both at 0 there is no line (''). Only fabrication orders have their own key, «+ N …» (hub, 2026-10-23),
// so the line never starts with a loose "and".
export const cascadeText = (cascade, t_) => {
  const documents = countOf(cascade.documents)
  const fabricationOrders = countOf(cascade.fabricationOrders)
  if (documents === 0 && fabricationOrders > 0) {
    return t_('Cascade_fabrication_orders_only', { count: fabricationOrders })
  }
  return [
    documents > 0 ? t_('Also protects its {{count}} documents', { count: documents }) : '',
    fabricationOrders > 0 ? t_('and {{count}} fabrication orders', { count: fabricationOrders }) : ''
  ].filter((part) => part !== '').join(' ')
}

// C11.5 (H-12), also the side summary: the marked screens by their translated name, separated as the language does.
export const protectedText = (screens, t_, language) => {
  const names = screens.filter((row) => row.sensitive === true).map((row) => t_(row.name))
  if (names.length === 0) {
    return t_('The items marked as protected')
  }
  const list = new Intl.ListFormat(intlLocale(language), { style: 'short', type: 'unit' }).format(names)
  return t_('{{screens}} and the items marked as protected', { screens: list })
}

const modelOf = (value) => MODELS.find((model) => model.value === String(value || '').toUpperCase())

// C11.2 H-11: the state of the vision service as a coloured dot and a text; nothing until the summary arrives. No
// model loaded says «Waiting»: «Idle» already meant «Inactivo» elsewhere in MIC (hub, 2026-10-20).
export const serviceState = (summary) => {
  if (typeof summary.enabled !== 'boolean') {
    return { text: '', color: 'transparent' }
  }
  const service = summary.service
  if (summary.enabled !== true || !service) {
    return { text: 'Off', color: '#757575' }
  }
  if (service.reachable !== true) {
    return { text: 'Unavailable', color: '#F68D76' }
  }
  if (service.loading === true) {
    return { text: 'Loading', color: '#FF8F00' }
  }
  if (service.modelLoaded === true && modelOf(service.model)) {
    return { text: 'Ready ({{model}})', model: modelOf(service.model).text, color: '#7FE1A7' }
  }
  return { text: 'Waiting', color: '#BDBDBD' }
}

// C3.1 (E13): Sun rejects an invalid VISION_ setting with a literal that carries the setting; its i18n key has the gap
// <KEY>, filled with the real setting. Any other message is its own key.
const INVALID_SETTING = /^Invalid camera vigilance setting: (\S+)$/

export const settingsMessageText = (message, t_) => {
  // C17.10: a notice with a count travels as its key and its options; one already translated (S414), as its text.
  if (typeof message === 'object') {
    return message.text || t_(message.key, message.options)
  }
  const invalid = INVALID_SETTING.exec(message)
  if (invalid) {
    return t_('Invalid camera vigilance setting: <KEY>').replace('<KEY>', invalid[1])
  }
  return t_(message)
}

// C14.1 (DR-4, PO 2026-10-22): the profiles that configure camera control, decided by the profile code as BACKOFFICE does,
// never by a permission. ADMIN reaches this screen only; the rest of BACKOFFICE stays SUPERADMIN.
export const CAMERA_CONTROL_PROFILES = ['ADMIN', 'SUPERADMIN']
// RN114 (E2): the covers that are a FAILURE of the vigilance — the camera (also a denied permission) and the service or
// the model. A phone, nobody at the screen or the camera turning on are the vigilance working (PO, 2026-10-26).
export const FAILURE_COVER_REASONS = ['NO_CAMERA', 'SERVICE_UNAVAILABLE']

// Reopening 4 (C16.6, C16.10, RN141, RN147): the maximum of watched sessions (2 when Sun has none) and whether a session
// without vigilance sees what is protected (NO by default, the safe side).
export const VISION_MAX_WATCHED_SESSIONS = 'VISION_MAX_WATCHED_SESSIONS'
export const VISION_UNWATCHED_SHOWS_PROTECTED = 'VISION_UNWATCHED_SHOWS_PROTECTED'
export const DEFAULT_MAX_WATCHED_SESSIONS = 2

// Reopening 5 (C17.6, RN150, RN155, RN157): whether the phone image of a PHONE incident is saved (NO by default, the safe
// side), whether faces are blurred (YES by default) and how many days it is kept (30).
export const VISION_PHONE_EVIDENCE = 'VISION_PHONE_EVIDENCE'
export const VISION_PHONE_EVIDENCE_BLUR_FACES = 'VISION_PHONE_EVIDENCE_BLUR_FACES'
export const VISION_PHONE_EVIDENCE_DAYS = 'VISION_PHONE_EVIDENCE_DAYS'
export const DEFAULT_PHONE_EVIDENCE_DAYS = 30

// C16.4, C16.8 (S341): how each state of a session is painted and what can be done with it. NONE (just entered) is
// "Waiting" until its first image (hub, 2026-10-26); NO_CAMERA does not take a slot, so it has no action (RN145).
export const SESSION_STATUS = {
  WATCHED: { text: 'Watching', action: 'stop', color: 'primary.main', bgcolor: '#2B3A31', border: '#2B3A31' },
  WAITING: { text: 'Waiting', action: 'watch', color: '#E6E6E6', bgcolor: '#4E4E4E', border: '#4E4E4E' },
  NONE: { text: 'Waiting', action: 'watch', color: '#E6E6E6', bgcolor: '#4E4E4E', border: '#4E4E4E' },
  RELEASED: { text: 'Not watched', action: 'watch', color: '#BDBDBD', bgcolor: 'transparent', border: '#6A6A6A' },
  NO_CAMERA: { text: 'No camera', action: '', color: '#F68D76', bgcolor: '#3A2A27', border: '#3A2A27' }
}

export const canConfigureCameraControl = (user) => Boolean(user && user.profile && CAMERA_CONTROL_PROFILES.includes(user.profile.code))

// C13.1, C13.7 (RN91): the scope of the vigilance, chosen with two cards. Read as Sun reads it: case-insensitive, and an
// unknown value is SENSITIVE (H10). Reopening 4 (RN138): the camera is on while MIC is open in both scopes.
export const VISION_SCOPE = 'VISION_SCOPE'
export const SCOPES = [
  { value: 'SENSITIVE', text: 'Protected content', line: 'The camera is on while MIC is open. Only protected content is hidden' },
  { value: 'ALL_MIC', text: 'All of MIC', line: 'The camera is on while MIC is open. Everything is hidden. Without a webcam, MIC cannot be used' }
]
export const scopeOf = (stored) => (String(stored || '').toUpperCase() === 'ALL_MIC' ? 'ALL_MIC' : 'SENSITIVE')

// C13.7: "nothing protected" = the six keys of summary.marks at 0 (no screen marked and no padlock); unknown until it arrives.
export const nothingProtected = (summary) => {
  const marks = summary.marks
  return Boolean(marks) && ['SCREEN', 'DOCUMENT', 'WIDGET', 'PART_REFERENCE', 'ORDER', 'FABRICATION_ORDER'].every((type) => marks[type] === 0)
}
